<template>
	<section
		class="scene-place-content"
		v-bind:class="{
			'can-favorite': $feature('venue_favoriting'),
			'expanded-gallery': expandedGallery,
			'has-scrolled': hasScrolled,
		}"
	>
		<section class="content-container" v-bind:key="venueModel.uniqueKey">
			<ViewVenueGallery
				v-bind:venueModel="venueModel"
				v-if="$feature('venue_gallery')"
				v-on:tap="Handle_OnTapGallery"
			/>

			<ViewVenueHeader
				v-bind:showFavorite="$feature('venue_favoriting')"
				v-bind:showOpenClosed="$feature('venue_openclosed')"
				v-bind:venueModel="venueModel"
				v-bind:class="{ 'show-favorite': $feature('venue_favoriting') }"
			/>

			<!-- Feature: Actions -->
			<ViewVenueActions v-bind:venueModel="venueModel" />

			<!-- Feature: Description -->
			<ViewVenueDescription v-bind:venueModel="venueModel" v-if="$feature('venue_description')" />

			<!-- Feature: Formatted Details -->
			<ChalkyVenueFormattedDetails v-bind:venueModel="venueModel" v-if="$feature('venue_formatteddetails')">
				<template v-slot:after>
					<div class="list-item" v-if="venueModel.getRevisionSummary().length > 5">
						<img class="icon filter-invert" src="~@chalkysticks/sass/build/asset/image/icon/chat.svg" />
						<h3 class="title">From the community</h3>
						<p class="description">&ldquo;{{ venueModel.getRevisionSummary() }}&rdquo;</p>
						<small>* Summary of community contributions</small>
					</div>
				</template>
			</ChalkyVenueFormattedDetails>

			<!-- Feature: Revisions -->
			<ViewVenueRevision class="push-bottom" v-bind:venueModel="venueModel" v-if="$feature('venue_revision')" />

			<!-- Feature: Checkins -->
			<ViewVenueCheckins
				ref="checkins"
				v-bind:venueModel="venueModel"
				v-if="$feature('venue_checkin')"
				v-on:user:select="Handle_OnSelectUser"
			/>

			<!-- Feature: Comments -->
			<ViewVenueComments ref="comments" v-if="$feature('venue_comments')" v-bind:venueModel="venueModel" />

			<!-- Feature: Friendly Faces -->
			<ViewVenueFriendlyFaces
				v-bind:venueModel="venueModel"
				v-if="$feature('venue_friendlyfaces')"
				v-on:submit:media="Handle_OnRequestSubmitMedia"
			/>

			<!-- Feature: Play the Rack -->
			<ViewVenuePlayTheRack
				ref="playTheRack"
				v-bind:venueModel="venueModel"
				v-if="$feature('venue_playtherack')"
				v-on:play="Handle_OnClickPlay"
			/>

			<!-- Feature: Hours -->
			<ViewVenueHours v-bind:venueModel="venueModel" v-if="$feature('venue_hours')" />

			<!-- Feature: Map -->
			<ViewVenueMap v-bind:venueModel="venueModel" v-if="$feature('venue_map')" />

			<!-- Feature: Venues Nearby -->
			<ViewVenueNearby
				v-bind:venueCollection="nearbyVenueCollection"
				v-bind:venueModel="venueModel"
				v-if="$feature('venue_nearby')"
				v-on:venue:select="Handle_OnSelectVenue"
				view="horizontal"
			/>
		</section>
	</section>
</template>

<script lang="ts">
	import * as Mixins from '@/Mixins';
	import ChalkySticks from '@chalkysticks/sdk';
	import SceneBase from '@/Core/Scene.vue';
	import { Component, Prop, Ref } from 'vue-property-decorator';

	/**
	 * Main content scene for displaying venue/place details including gallery,
	 * header, actions, checkins, comments, and nearby venues
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Scene/Place
	 * @project ChalkySticks App
	 */
	@Component({
		mixins: [Mixins.StickyScroll],
	})
	export default class ScenePlaceContent extends SceneBase {
		/**
		 * Reference to the checkins component for scrolling
		 *
		 * @type any
		 */
		@Ref('checkins')
		readonly checkins!: any;

		/**
		 * Reference to the play the rack component
		 *
		 * @type any
		 */
		@Ref('playTheRack')
		readonly playTheRack!: any;

		/**
		 * The venue model to display
		 *
		 * @type ChalkySticks.Model.Venue
		 */
		@Prop({ required: true })
		public venueModel!: ChalkySticks.Model.Venue;

		/**
		 * Collection of nearby venues
		 *
		 * @type ChalkySticks.Collection.Venue
		 */
		@Prop({ required: true })
		public nearbyVenueCollection!: ChalkySticks.Collection.Venue;

		/**
		 * Whether the gallery is expanded
		 *
		 * @type boolean
		 */
		protected expandedGallery: boolean = false;

		/**
		 * Configuration for sticky behavior
		 * Uses 'page' scrollContainer since the Page element is what scrolls,
		 * not this Scene element
		 *
		 * @type IStickyScrollConfig
		 */
		protected stickyConfig: any = {
			scrollContainer: 'page',
			scrollThreshold: 100,
		};

		/**
		 * Gets the inverse rack positions from the play the rack component
		 *
		 * @return any
		 */
		public getInverseRackPositions(): any {
			return this.playTheRack?.getInverseRackPositions();
		}

		/**
		 * Scrolls to the checkins section
		 *
		 * @return void
		 */
		public scrollToCheckins(): void {
			this.$nextTick(() => {
				const scannerElement = this.checkins as HTMLElement;

				scannerElement?.scrollIntoView({
					behavior: 'auto',
					block: 'center',
				});
			});
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Called when user clicks play in PlayTheRack component
		 *
		 * @return void
		 */
		protected Handle_OnClickPlay(): void {
			this.$emit('play');
		}

		/**
		 * Called when user requests to submit media
		 *
		 * @return void
		 */
		protected Handle_OnRequestSubmitMedia(): void {
			this.$emit('submit:media');
		}

		/**
		 * Called when a user is selected from checkins
		 *
		 * @param ChalkySticks.Model.User userModel
		 * @return void
		 */
		protected Handle_OnSelectUser(userModel: ChalkySticks.Model.User): void {
			this.$emit('user:select', userModel);
		}

		/**
		 * Called when a venue is selected from nearby
		 *
		 * @param ChalkySticks.Model.Venue venueModel
		 * @return void
		 */
		protected Handle_OnSelectVenue(venueModel: ChalkySticks.Model.Venue): void {
			this.$emit('venue:select', venueModel);
		}

		/**
		 * Called when gallery is tapped
		 *
		 * @param Event e
		 * @return void
		 */
		protected Handle_OnTapGallery(e: Event): void {
			this.expandedGallery = !this.expandedGallery;
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.scene-place-content {
		> .content-container {
			position: relative;

			.venue-formatteddetails:not(.has-details) {
				display: none;
			}

			> .view-venue-revision {
				// mk: So the modal can sit above everything else
				position: initial;
			}
		}

		.venue-gallery {
			filter: blur(0);
			position: sticky;
			top: -100px;
			transform-origin: top center;
			transition: height 0.5s var(--ease-in-out-cubic), filter 0.75s ease, opacity 0.5s ease, transform 0.5s ease;
			z-index: 0;

			figcaption {
				transition: opacity 0.3s ease;
			}
		}

		&.has-scrolled .venue-gallery {
			filter: blur(20px);
			opacity: 0.75;
			transform: translateY(-0px);

			figcaption {
				opacity: 0;
			}
		}
	}

	// State
	// ---------------------------------------------------------------------------

	.scene-place-content.expanded-gallery {
		.venue-gallery {
			height: calc(100svh - var(--global-tab-height));
		}
	}
</style>
